import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useContentStudioApi, StudioProp } from '../../../../common/hooks/use-content-studio-api.hook';

/**
 * The 3D studios' furniture library — one shelf, two studios.
 *
 * Both the furniture studio and the surface studio place real meshes beside
 * their subject, and both need the same things: the shared catalogue, the
 * vendor's own pieces, generation-from-a-photo with its polling, and a picker.
 * That is a lot of behaviour to keep identical in two 2,000-line files, so it
 * lives here instead and each studio supplies only what differs — where a
 * placed piece goes in ITS scene.
 */

/** The bucket a vendor's own generated pieces land in. */
export const MY_LIBRARY = 'my library';

/**
 * Words that are initialisms, rather than words that happen to be short.
 *
 * A "two letters or fewer means uppercase" rule reads the language as
 * acronyms: right for "tv stands", wrong for "my library".
 */
const CATEGORY_ACRONYMS = new Set(['tv', 'av', 'pc', '3d', 'led', 'diy']);

export const titleCase = (slug: string): string => slug
    .split(/[\s-]+/)
    .map((w) => (CATEGORY_ACRONYMS.has(w.toLowerCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');

export interface StudioPropLibrary {
    categories: string[];
    category: string;
    setCategory: (c: string) => void;
    items: StudioProp[];
    loading: boolean;
    uploading: boolean;
    /** Fetches once; safe to call on every open. */
    open: () => void;
    uploadFromPhoto: (file: File) => Promise<void>;
    removeMine: (propId: string) => Promise<void>;
}

/**
 * Library state: what is on the shelf, and what is still being built.
 *
 * @param onError shown by the host studio, which owns the error banner.
 */
export function useStudioPropLibrary(onError: (message: string) => void): StudioPropLibrary {
    const studioApi = useContentStudioApi();
    const [categories, setCategories] = useState<string[]>([]);
    const [category, setCategory] = useState<string>('');
    const [items, setItems] = useState<StudioProp[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const asked = useRef(false);

    /**
     * Generating a prop from the vendor's own photograph.
     *
     * Polling is not only for the badge: the server finalises the prop on the
     * first poll that finds the job done, copying the GLB into the library
     * before the generator's own copy expires — so a picker that never polled
     * would leave the vendor a library entry pointing at nothing.
     */
    const polls = useRef<Map<string, number>>(new Map());

    const poll = useCallback((propId: string) => {
        if (polls.current.has(propId)) return;
        const check = async () => {
            try {
                const res = await studioApi.getStudioPropStatus(propId);
                if (res.status === 'generating') return;
                window.clearInterval(polls.current.get(propId));
                polls.current.delete(propId);
                setItems((l) => l.map((p) => (
                    p.propId === propId
                        ? { ...p, status: res.status, previewUrl: res.previewUrl || p.previewUrl, error: res.error }
                        : p
                )));
            } catch {
                // A failed poll is not a failed prop — the next tick retries.
            }
        };
        polls.current.set(propId, window.setInterval(check, 5000));
        // Check once now rather than waiting out the first interval: a prop
        // being re-armed on load has usually finished already.
        void check();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Timers outlive the dialog otherwise, polling for a studio nobody is in.
    useEffect(() => () => {
        polls.current.forEach((t) => window.clearInterval(t));
        polls.current.clear();
    }, []);

    const open = useCallback(() => {
        if (asked.current) return;
        asked.current = true;
        setLoading(true);
        Promise.all([studioApi.listStudioPropCategories(), studioApi.listStudioProps()])
            .then(([cats, list]) => {
                /**
                 * The vendor's own shelf leads, and the rest keep the API's order.
                 *
                 * Safe as the opening tab because the backend only reports it once
                 * they own something in it — so it can never be a leading empty tab.
                 */
                const ordered = [
                    ...cats.filter((c) => c === MY_LIBRARY),
                    ...cats.filter((c) => c !== MY_LIBRARY),
                ];
                setCategories(ordered);
                setItems(list);
                if (ordered.length) setCategory(ordered[0]);
                // Pick the watch back up for anything still building: a job that
                // finished while the tab was shut would otherwise stay "Building…"
                // for ever, over a mesh that has been ready for twenty minutes.
                list.filter((p) => p.status === 'generating').forEach((p) => poll(p.propId));
            })
            .catch(() => { asked.current = false; })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [poll]);

    const uploadFromPhoto = useCallback(async (file: File) => {
        setUploading(true);
        try {
            const created = await studioApi.createStudioProp(file, { name: file.name });
            setItems((l) => [{
                propId: created.propId,
                name: file.name.replace(/\.[^.]+$/, ''),
                category: MY_LIBRARY,
                status: 'generating',
                mine: true,
            } as StudioProp, ...l]);
            setCategories((c) => (c.includes(MY_LIBRARY) ? c : [MY_LIBRARY, ...c]));
            setCategory(MY_LIBRARY);
            poll(created.propId);
        } catch (err: any) {
            onError(err?.message || 'Could not start the 3D generation');
        } finally {
            setUploading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [poll, onError]);

    const removeMine = useCallback(async (propId: string) => {
        setItems((l) => l.filter((p) => p.propId !== propId));
        try { await studioApi.deleteStudioProp(propId); } catch { /* already gone is fine */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { categories, category, setCategory, items, loading, uploading, open, uploadFromPhoto, removeMine };
}

/** A category tab. Local so this module does not depend on a studio widget. */
const CategoryPill: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
    <button
        type="button"
        onClick={onClick}
        className={cn(
            'cursor-pointer whitespace-nowrap rounded-md border px-2 py-1 text-[11px] font-medium transition-colors',
            active
                ? 'border-foreground bg-muted text-foreground'
                : 'bg-background text-muted-foreground hover:text-foreground',
        )}
    >
        {children}
    </button>
);

/**
 * The picker: make-from-photo, category tabs, and a two-column shelf.
 *
 * Placement is the host's job — a piece lands on the furniture studio's floor
 * or the surface studio's built room, and only they know where that is.
 */
export const StudioPropLibraryPanel: React.FC<{
    library: StudioPropLibrary;
    onPlace: (item: StudioProp) => void;
}> = ({ library, onPlace }) => {
    const { t } = useTranslation();
    const fileRef = useRef<HTMLInputElement>(null);
    const { categories, category, setCategory, items, loading, uploading } = library;
    const shown = items.filter((p) => p.category === category);

    return (
        <>
            {/* Make one from a photograph. The vendor's own pieces are private
                and land in My library. */}
            <div className="px-3 pt-2.5">
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (f) void library.uploadFromPhoto(f);
                    }}
                />
                <button
                    type="button"
                    disabled={uploading}
                    onClick={() => fileRef.current?.click()}
                    className={cn(
                        'flex w-full items-center justify-center gap-1.5 rounded-lg border-[1.5px] border-dashed border-border bg-muted py-2 text-[11.5px] text-muted-foreground transition-colors hover:border-foreground hover:text-foreground',
                        uploading ? 'cursor-wait' : 'cursor-pointer',
                    )}
                >
                    <ImagePlus className="size-4" />
                    {uploading
                        ? t('CONTENT_STUDIO.STUDIO_PROP_UPLOADING', 'Sending…')
                        : t('CONTENT_STUDIO.STUDIO_PROP_FROM_IMAGE', 'Make one from a photo')}
                </button>
            </div>

            {/* Categories, from the data — a new bucket in the collection
                appears here without a deploy. */}
            <div className="flex flex-wrap gap-1 border-b px-3 py-2.5">
                {categories.map((cat) => (
                    <CategoryPill key={cat} active={category === cat} onClick={() => setCategory(cat)}>
                        {titleCase(cat)}
                    </CategoryPill>
                ))}
            </div>

            <div className="flex-1 overflow-y-auto p-2.5">
                {loading && (
                    <div className="py-6 text-center text-[11.5px] text-muted-foreground">
                        {t('CONTENT_STUDIO.STUDIO_PROPS_LOADING', 'Loading the library…')}
                    </div>
                )}
                {!loading && shown.length === 0 && (
                    <div className="py-6 text-center text-[11.5px] text-muted-foreground">
                        {t('CONTENT_STUDIO.STUDIO_PROPS_EMPTY', 'Nothing in this category yet.')}
                    </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                    {shown.map((item) => {
                        const notReady = item.status === 'generating' || item.status === 'failed';
                        return (
                            <div
                                key={item.propId}
                                // Nothing to put in the scene until the mesh exists.
                                draggable={!notReady}
                                onDragStart={(e: React.DragEvent) => {
                                    e.dataTransfer.setData('application/x-studio-prop', item.propId);
                                    e.dataTransfer.effectAllowed = 'copy';
                                }}
                                onClick={() => { if (!notReady) onPlace(item); }}
                                title={item.status === 'failed' ? (item.error || 'Generation failed') : item.name}
                                className="cursor-grab overflow-hidden rounded-xl border bg-card transition-colors hover:border-foreground active:cursor-grabbing"
                            >
                                {item.status === 'generating' ? (
                                    <div className="flex h-24 w-full flex-col items-center justify-center gap-1 bg-muted text-muted-foreground">
                                        <Loader2 className="size-[18px] animate-spin" />
                                        <div className="text-[9.5px]">{t('CONTENT_STUDIO.STUDIO_PROP_BUILDING', 'Building…')}</div>
                                    </div>
                                ) : item.status === 'failed' ? (
                                    <div className="flex h-24 w-full items-center justify-center bg-destructive/10 px-2 text-center text-[9.5px] text-destructive">
                                        {t('CONTENT_STUDIO.STUDIO_PROP_FAILED', 'Could not build this one')}
                                    </div>
                                ) : item.previewUrl ? (
                                    <img
                                        src={item.previewUrl}
                                        alt={item.name}
                                        loading="lazy"
                                        draggable={false}
                                        className="block h-24 w-full bg-muted object-cover"
                                    />
                                ) : (
                                    <div className="h-24 w-full bg-muted" />
                                )}
                                <div className="flex items-center gap-0.5 px-1.5 py-1.5">
                                    <div className="min-w-0 flex-1 truncate text-[10.5px] text-muted-foreground">
                                        {item.name}
                                    </div>
                                    {item.mine && (
                                        <button
                                            type="button"
                                            onClick={(e: React.MouseEvent) => { e.stopPropagation(); void library.removeMine(item.propId); }}
                                            title={t('CONTENT_STUDIO.STUDIO_REMOVE', 'Remove') as string}
                                            className="cursor-pointer leading-none text-muted-foreground transition-colors hover:text-destructive"
                                        >
                                            <Trash2 className="size-3.5" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
};
